import { type } from 'arktype';
import { isoDateSchema } from './project.model';

/**
 * Models of the "Zeiterfassung (eigene Person)" surface (spec:
 * project-time-tracking.md, stories 6–7): time entries, day context,
 * bookable projects, and the server-side stopwatch.
 */

/** How a time entry was captured. */
export const TIME_ENTRY_SOURCES = ['manual', 'timer'] as const;
export type TimeEntrySource = (typeof TIME_ENTRY_SOURCES)[number];

/**
 * A project time booking of the calling person, enriched with display data
 * (project number/name, activity name) for the booking lists.
 */
export interface ProjectTimeEntry {
  id: string;
  personId: string;
  projectId: string;
  activityTypeId: string;
  /** Booking day (`YYYY-MM-DD`). */
  entryDate: string;
  /** Booked minutes (UI works in hours with step 0.25 = 15 minutes). */
  minutes: number;
  /** Billable snapshot at booking time (prefilled from the activity type, overridable). */
  billable: boolean;
  comment: string | null;
  source: TimeEntrySource;
  /** Approval state (spec: faktura.md, Story 3). */
  reviewStatus: 'draft' | 'approved';
  /** Set once invoiced (spec: faktura.md, Story 4/7) — `null` while not yet on an invoice. */
  invoicedAt: string | null;
  /** Id of the invoice this booking was billed on. `null` while not yet invoiced. */
  invoiceId: string | null;
  /** Number of the invoice this booking was billed on ("RE-<year>-<counter>"). `null` while not yet invoiced. */
  invoiceNumber: string | null;
  projectNumber: string;
  projectName: string;
  activityTypeName: string;
  createdAt: string;
  updatedAt: string;
}

const minutesSchema = type('1 <= number.integer <= 1440').configure({
  description: 'Booked minutes. The UI works in 0.25 h steps (15 minutes); the API accepts any integer between 1 and 1440.',
  examples: [90],
});

const commentSchema = type('string <= 1000 | null').configure({
  description: 'Optional free-text comment. `null` clears the field.',
  examples: ['Sprint-Planning vorbereitet'],
});

const projectIdSchema = type('string.uuid').configure({ description: 'Id of the (leaf) project to book on.' });
const activityTypeIdSchema = type('string.uuid').configure({ description: 'Id of an activity type assigned to the project.' });

export const createTimeEntrySchema = type({
  projectId: projectIdSchema,
  activityTypeId: activityTypeIdSchema,
  entryDate: isoDateSchema,
  minutes: minutesSchema,
  'billable?': type('boolean').configure({
    description: 'Billable flag. Defaults to the billable flag of the activity type when omitted.',
  }),
  'comment?': commentSchema,
}).configure({ description: 'Payload for creating a project time entry (always for the calling person).' });
export type CreateTimeEntry = typeof createTimeEntrySchema.t;

export const updateTimeEntrySchema = type({
  'projectId?': projectIdSchema,
  'activityTypeId?': activityTypeIdSchema,
  'entryDate?': isoDateSchema,
  'minutes?': minutesSchema,
  'billable?': 'boolean',
  'comment?': commentSchema,
}).configure({ description: 'Partial update for an own project time entry.' });
export type UpdateTimeEntry = typeof updateTimeEntrySchema.t;

/**
 * Day banner of the booking screen: actual worked time (from the time
 * tracking), already distributed project time, and the open rest.
 */
export interface ProjectDayContext {
  /** The requested day (`YYYY-MM-DD`). */
  date: string;
  /** Net worked minutes of the day from the time-tracking ledger (read-only). */
  workedMinutes: number;
  /** Sum of the person's project time entries of the day, in minutes. */
  distributedMinutes: number;
  /** `workedMinutes - distributedMinutes`; negative = distributed above worked time (warning, no block). */
  restMinutes: number;
}

/** Activity type assigned to a bookable project (selection list entry). */
export interface BookableActivity {
  activityTypeId: string;
  name: string;
  /** Default for the billable toggle of the booking form. */
  billable: boolean;
}

/**
 * A project the calling person may book on: assigned (member or responsible),
 * status `active`, and a leaf (no child projects). Includes the assigned,
 * non-archived activity types as the combobox source.
 */
export interface BookableProject {
  id: string;
  number: string;
  name: string;
  startDate: string | null;
  endDate: string | null;
  activities: BookableActivity[];
}

/** The person's running stopwatch, including display context for the timer pill. */
export interface ProjectTimerInfo {
  /** Start instant (ISO 8601). */
  startedAt: string;
  projectId: string | null;
  activityTypeId: string | null;
  comment: string | null;
  billable: boolean | null;
  projectNumber: string | null;
  projectName: string | null;
  activityTypeName: string | null;
}

export const startTimerSchema = type({
  'projectId?': type('string.uuid | null').configure({ description: 'Optional booking context: project id.' }),
  'activityTypeId?': type('string.uuid | null').configure({ description: 'Optional booking context: activity type id.' }),
  'comment?': commentSchema,
  'billable?': type('boolean | null').configure({ description: 'Optional booking context: billable override.' }),
}).configure({ description: 'Optional booking context captured when starting the stopwatch.' });
export type StartTimer = typeof startTimerSchema.t;

/**
 * One per-day booking proposal produced by stopping the stopwatch. Days
 * without recorded working time are marked `hasWorkedTime: false` so the
 * confirmation dialog can flag them — the measurement is never discarded.
 */
export interface TimerProposal {
  /** Calendar day (`YYYY-MM-DD`). */
  date: string;
  /** Proposed minutes, rounded to quarter hours (minimum 15 when the overlap is > 0). */
  minutes: number;
  /** Whether the day has recorded work intervals in the time tracking. */
  hasWorkedTime: boolean;
}

/**
 * Result of stopping the stopwatch: the captured context plus one proposal
 * per spanned calendar day. Stopping never creates entries — the client
 * confirms via regular `POST /project-time/entries` calls.
 */
export interface TimerStopResult {
  startedAt: string;
  stoppedAt: string;
  context: {
    projectId: string | null;
    activityTypeId: string | null;
    comment: string | null;
    billable: boolean | null;
  };
  proposals: TimerProposal[];
}
